
import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Calendar,
  Clock,
  X,
  Check,
  ArrowLeft,
} from "lucide-react";
import { authenticatedFetch } from '../services/auth';
import { API_BASE } from '../utils/apiConfig';

const PeriodManagement = ({ onClose }) => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [formData, setFormData] = useState({
    period_code: "",
    period_month: "",
    start_date: "",
    end_date: "",
    description: "",
    is_active: true,
  });

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    console.log("Buscando períodos...");
    try {
      const response = await authenticatedFetch(
        `${API_BASE}/api/periods-management`,
      );
      if (response.ok) {
        const data = await response.json();
        console.log("✅ Períodos carregados:", data);
        setPeriods(data || []);
      } else {
        console.error("Erro ao buscar períodos:", response.status);
      }
    } catch (error) {
      console.error("Erro ao carregar períodos:", error);
    } finally {
      setLoading(false);
    }
  };

  // Função para gerar código do período baseado na data
  const generatePeriodCode = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  // Função para calcular primeiro e último dia do mês
  const calculateMonthDates = (yearMonth) => {
    if (!yearMonth || !yearMonth.includes('-')) return { start: "", end: "" };

    const [year, month] = yearMonth.split('-');
    const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
    const endDate = new Date(parseInt(year), parseInt(month), 0);

    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };

    return {
      start: formatDate(startDate),
      end: formatDate(endDate)
    };
  };

  // Handler para mudança do campo período
  const handlePeriodChange = (periodValue) => {
    const dates = calculateMonthDates(periodValue);
    setFormData({
      ...formData,
      period_month: periodValue,
      period_code: periodValue,
      start_date: dates.start,
      end_date: dates.end
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingPeriod
        ? `${API_BASE}/api/periods-management/${editingPeriod.period_id}`
        : `${API_BASE}/api/periods-management`;

      const method = editingPeriod ? "PUT" : "POST";

      const payload = {
        ...formData,
        created_by: editingPeriod ? undefined : "Sistema",
      };

      const response = await authenticatedFetch(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await fetchPeriods();
        handleCloseModal();
        alert(
          editingPeriod
            ? "Período atualizado com sucesso!"
            : "Período criado com sucesso!",
        );
      } else {
        const errorData = await response.json();
        alert(`Erro: ${errorData.message || "Erro desconhecido"}`);
      }
    } catch (error) {
      console.error("Erro ao salvar período:", error);
      alert("Erro ao salvar período");
    }
  };

  const handleEdit = (period) => {
    setEditingPeriod(period);
    const periodCode = period.period_code || generatePeriodCode(period.start_date);
    setFormData({
      period_code: periodCode,
      period_month: periodCode,
      start_date: period.start_date ? period.start_date.split("T")[0] : "",
      end_date: period.end_date ? period.end_date.split("T")[0] : "",
      description: period.description || "",
      is_active: period.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (period) => {
    if (
      !window.confirm(
        `Tem certeza que deseja desativar o período "${period.period_code}"?`,
      )
    ) {
      return;
    }

    try {
      const response = await authenticatedFetch(
        `${API_BASE}/api/periods-management/${period.period_id}`,
        {
          method: "DELETE",
        },
      );

      if (response.ok) {
        await fetchPeriods();
        alert("Período desativado com sucesso!");
      } else {
        const errorData = await response.json();
        alert(`Erro: ${errorData.message || "Erro desconhecido"}`);
      }
    } catch (error) {
      console.error("Erro ao deletar período:", error);
      alert("Erro ao deletar período");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPeriod(null);
    setFormData({
      period_code: "",
      period_month: "",
      start_date: "",
      end_date: "",
      description: "",
      is_active: true,
    });
  };

  const filteredPeriods = periods.filter(
    (period) =>
      period.period_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      period.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const formatDate = (dateString) => {
    if (!dateString) return "";
    // Extrai apenas a parte da data (YYYY-MM-DD) ignorando timezone
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Carregando períodos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="h-full">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="px-4 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">

                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Gestão de Períodos</h1>
                    <p className="text-gray-600 text-sm">Gerencie os períodos do calendário BSC</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowModal(true)}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Plus className="w-5 h-5" />
                Novo Período
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 lg:px-8 py-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200">
            {/* Search Bar */}
            <div className="p-6 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por código ou descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-lg"
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Data Início
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Data Fim
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPeriods.map((period) => (
                    <tr key={period.period_id} className="hover:bg-blue-50 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{period.period_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="p-2 bg-orange-100 rounded-lg mr-3">
                            <Clock className="w-4 h-4 text-orange-600" />
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            {period.period_code}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(period.start_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(period.end_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                        <div className="truncate" title={period.description}>
                          {period.description || "-"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${period.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                            }`}
                        >
                          {period.is_active ? "Ativo" : "Inativo"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleEdit(period)}
                            className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200"
                            title="Editar período"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(period)}
                            className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50 transition-colors duration-200"
                            title="Desativar período"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredPeriods.length === 0 && (
              <div className="text-center py-16">
                <div className="p-4 bg-gray-100 rounded-full inline-block mb-4">
                  <Clock className="w-12 h-12 text-gray-400" />
                </div>
                <p className="text-gray-500 text-lg font-medium">Nenhum período encontrado</p>
                <p className="text-gray-400 mt-2">Tente ajustar os filtros de busca</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Calendar className="w-6 h-6 text-orange-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {editingPeriod ? "Editar Período" : "Novo Período"}
                  </h2>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Período *
                </label>
                <input
                  type="month"
                  required
                  value={formData.period_month}
                  onChange={(e) => handlePeriodChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Selecione o mês/ano do período
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Código do Período
                </label>
                <input
                  type="text"
                  value={formData.period_code}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-100 text-gray-600"
                  placeholder="Será gerado automaticamente"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Gerado automaticamente no formato YYYY-MM
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Data de Início
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    readOnly
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-100 text-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Data de Fim
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    readOnly
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-100 text-gray-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                  rows="3"
                  placeholder="Descrição do período"
                />
              </div>

              {editingPeriod && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="mr-3 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Período ativo
                  </label>
                </div>
              )}

              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-semibold transition-colors duration-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {editingPeriod ? "Atualizar" : "Criar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodManagement;
